import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import { useConfig } from '../contexts/ConfigContext.js';
import { getAuth, onAuthStateChanged } from "firebase/auth";

import { collection, getDoc, getDocs, doc } from "firebase/firestore";
import { db } from '../firebase.js';

const Players = () => {
  const navigate = useNavigate();
  const { playerID } = useParams();
  const config = useConfig();
  const SEASON = config.currentSeason;
  const [dataReady, setDataReady] = useState(false);
  const [playerView, setPlayerView] = useState({});
  const [careerRecord, setCareerRecord] = useState({});
  const [badgeTypes, setBadgeTypes] = useState([]);
  const [currentUser, setCurrentUser] = useState({})
  const [activeBadge, setActiveBadge] = useState(null);


  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setCurrentUser(currentUser)

      getPlayerView()
    });

    return () => unsubscribe();
  }, [])

  const getPlayerView = async () => {
    const [playerViewSnapshot, careerRecordSnapshot, badgeTypesSnapshot] = await Promise.all([
      getDoc(doc(db, "playerViews", playerID)),
      getDoc(doc(db, "records", playerID)),
      getDocs(collection(db, "badgeTypes"))
    ]);

    setPlayerView(playerViewSnapshot.data());
    setCareerRecord(careerRecordSnapshot.exists() ? careerRecordSnapshot.data() : {});
    setBadgeTypes(badgeTypesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    setDataReady(true);
  }

  const badgeImageContext = require.context('../assets/badges', false, /\.(jpg|png|svg|webp)$/);
  const badgeImages = {};
  badgeImageContext.keys().forEach(key => {
    const name = key.replace('./', '').replace(/\.(jpg|png|svg|webp)$/, '');
    badgeImages[name] = badgeImageContext(key);
  });

  function displayBadges() {
    const badges = playerView.badges || [];
    if (badges.length === 0) return null;

    const badgeTypeMap = {};
    badgeTypes.forEach(bt => { badgeTypeMap[bt.id] = bt; });

    // Group badges by badgeID so each type shows once with all its seasons
    const grouped = {};
    badges.forEach(b => {
      if (!grouped[b.badgeID]) grouped[b.badgeID] = [];
      grouped[b.badgeID].push(b.season);
    });

    const rendered = Object.entries(grouped)
      .map(([badgeID, seasons]) => {
        const bt = badgeTypeMap[badgeID];
        const src = badgeImages[badgeID];
        if (!src) return null;
        const name = bt?.name || badgeID;
        const badgeType = bt?.type || 'award';
        const sortedSeasons = [...seasons].sort((a, b) => a - b);
        return (
          <div
            key={badgeID}
            style={{ flexShrink: 0, cursor: 'pointer' }}
            onClick={e => {
              e.stopPropagation();
              setActiveBadge({
                src,
                badgeType,
                name,
                sortedSeasons,
                description: bt?.description || '',
              });
            }}
          >
            <img src={src} alt={name} style={{ width: 75, height: 75, objectFit: 'contain', display: 'block' }} />
          </div>
        );
      })
      .filter(Boolean);

    if (rendered.length === 0) return null;

    return (
      <>
        <div className="league-table-container badges scroll-on-overflow">
          <div style={{ display: 'flex', gap: 16, width: 'max-content', margin: '0 auto' }}>
            {rendered}
          </div>
        </div>
        {activeBadge && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}
            onClick={() => setActiveBadge(null)}
          >
            <div
              style={{ background: '#fff', borderRadius: 12, padding: '32px 28px', maxWidth: 320, width: '90%', textAlign: 'center' }}
              onClick={e => e.stopPropagation()}
            >
              <img src={activeBadge.src} alt={activeBadge.name} style={{ width: 160, height: 160, objectFit: 'contain', marginBottom: 16 }} />
              {activeBadge.badgeType === 'achievement' ? (
                <>
                  <p style={{ fontWeight: 600, margin: '0 0 8px' }}>{activeBadge.name}</p>
                  {activeBadge.sortedSeasons.map(s => (
                    <p key={s} style={{ color: '#555', margin: '2px 0', fontSize: 14 }}>{activeBadge.description} - Season {s}</p>
                  ))}
                </>
              ) : (
                <>
                  <p style={{ fontWeight: 600, margin: '0 0 8px' }}>
                    {activeBadge.sortedSeasons.length > 1
                      ? `${activeBadge.name} x${activeBadge.sortedSeasons.length} - Seasons ${activeBadge.sortedSeasons.join(', ')}`
                      : `${activeBadge.name} - Season ${activeBadge.sortedSeasons[0]}`}
                  </p>
                  <p style={{ color: '#555', margin: 0, fontSize: 14 }}>{activeBadge.description}</p>
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  function displayAllSeasonsAverages () {
    const seasonAverages = [];

    for(let seasonIndex = 1; seasonIndex <= SEASON; seasonIndex++) {
      const season = seasonIndex.toString()

      if (season in playerView.seasonAverages) {
        seasonAverages.push(displaySeasonAverages(season))
      }
    }

    return seasonAverages;
  }

  function getSeasonTeams (season, seasonData, playerID) {
    return (
      seasonData.teams.map((team, team_number) => (
        <span key={ playerID + "-season-" + season + "-team- " + team.id + "-row" }>
          <a className="unstyled-link" href={"/teams/" + team.id }>
            <span>{ team.nameAbbv }</span>
          </a>
          { team_number != seasonData.teams.length - 1 ? ', ' : ''}
        </span>
      ))
    )
  }

  function displaySeasonAverages (season) {
    const seasonData = playerView.seasonAverages[season]

    const seasonTitle = season == 'all' ? '' : season;

    return (
        <tr key={ playerID + "-season-" + season + "-row" }>
          <td className="league-table-data-plain user-name"><span>{ seasonTitle }</span></td>
          <td className="league-table-data-plain user-name">
            {
              season == 'all' ? <></> : getSeasonTeams(season, seasonData, playerID)
            }
          </td>
          <td className="league-table-data wide"><span>{ seasonData.gamesPlayed }</span></td>
          <td className="league-table-data wide"><span>{ seasonData.points }</span></td>
          <td className="league-table-data wide"><span>{ seasonData.rebounds }</span></td>
          <td className="league-table-data wide"><span>{ seasonData.assists }</span></td>
          <td className="league-table-data wide"><span>{ seasonData.steals }</span></td>
          <td className="league-table-data wide"><span>{ seasonData.blocks }</span></td>
        </tr>
    )
  }

  function displayAllGameStats () {
    const seasonGameStats = [];

    for(let seasonIndex = SEASON; seasonIndex >= 1; seasonIndex--) {
      const season = seasonIndex.toString()

      if (season in playerView.seasonAverages) {
        seasonGameStats.push(displaySeasonGameStats(season))
      }
    }

    return seasonGameStats;
  }

  function displaySeasonGameStats (season) {
    const seasonGameStats = playerView.seasonStats[season]

    return (
      <>
        <p className="league-title-standard-small">Season { season }</p>
        <table className="table league-table">
          <thead className="league-table-heading-hole">
            <tr>
              <th className="league-table-heading-name hole"><span>TEAM</span></th>
              <th className="league-table-heading-name hole"><span>OPP</span></th>
              <th className="league-table-heading-name hole"><span>RES</span></th>
              <th className="league-table-heading hole"><span>PTS</span></th>
              <th className="league-table-heading hole"><span>RBS</span></th>
              <th className="league-table-heading hole"><span>AST</span></th>
              <th className="league-table-heading hole"><span>STL</span></th>
              <th className="league-table-heading hole"><span>BLK</span></th>
            </tr>
          </thead>
          <thead className="league-table-heading-total">
            <tr>
              <th className="league-table-heading-small"><span>TM</span></th>
              <th className="league-table-heading-small"><span>OPP</span></th>
              <th className="league-table-heading-small"><span>RESULT</span></th>
              <th className="league-table-heading-small"><span>PTS</span></th>
              <th className="league-table-heading-small"><span>RBS</span></th>
              <th className="league-table-heading-small"><span>AST</span></th>
              <th className="league-table-heading-small"><span>STL</span></th>
              <th className="league-table-heading-small"><span>BLK</span></th>
            </tr>
          </thead>
          <tbody>
            {
              seasonGameStats.map((gameStat) => (
                <tr key={playerID + "-" + gameStat.gameID + "-team-row-" + season}>
                  <td className="league-table-data-plain max-team-abbv-width user-name"><span><a className="unstyled-link" href={"/teams/" + gameStat.team.id }><span>{ gameStat.team.nameAbbv }</span></a></span></td>
                  <td className="league-table-data-plain max-team-abbv-width user-name"><span><a className="unstyled-link" href={"/teams/" + gameStat.opponent.id }><span>{ gameStat.opponent.nameAbbv }</span></a></span></td>
                  <td className="league-table-data-plain max-game-result-width user-name result"><a className="unstyled-link" href={"/games/" + gameStat.gameID }><span>{ gameStat.result } { gameStat.score }</span></a></td>
                  { displayGameStats(gameStat) }
                </tr>
              ))
            }
          </tbody>
        </table>
      </>
    )
  }

  function displayGameStats(stats) {
    if (stats.status == 'played') {
      const isCareerHigh = (key) =>
        stats[key] > 0 && careerRecord[key] > 0 && stats[key] >= careerRecord[key];

      return (
        <>
          <td className={`mobile-only league-table-data wide ${isCareerHigh('points') ? ' career-high-stat' : ''}`}><span>{ stats.points }</span></td>
          <td className={`mobile-only league-table-data wide ${isCareerHigh('rebounds') ? ' career-high-stat' : ''}`}><span>{ stats.rebounds }</span></td>
          <td className={`mobile-only league-table-data wide ${isCareerHigh('assists') ? ' career-high-stat' : ''}`}><span>{ stats.assists }</span></td>
          <td className={`mobile-only league-table-data wide ${isCareerHigh('steals') ? ' career-high-stat' : ''}`}><span>{ stats.steals }</span></td>
          <td className={`mobile-only league-table-data wide ${isCareerHigh('blocks') ? ' career-high-stat' : ''}`}><span>{ stats.blocks }</span></td>
          <td className={`desktop-only league-table-data-name stat-line ${isCareerHigh('points') ? ' career-high-stat' : ''}`}><p>{ stats.points }</p></td>
          <td className={`desktop-only league-table-data-name stat-line ${isCareerHigh('rebounds') ? ' career-high-stat' : ''}`}><p>{ stats.rebounds }</p></td>
          <td className={`desktop-only league-table-data-name stat-line ${isCareerHigh('assists') ? ' career-high-stat' : ''}`}><p>{ stats.assists }</p></td>
          <td className={`desktop-only league-table-data-name stat-line ${isCareerHigh('steals') ? ' career-high-stat' : ''}`}><p>{ stats.steals }</p></td>
          <td className={`desktop-only league-table-data-name stat-line ${isCareerHigh('blocks') ? ' career-high-stat' : ''}`}><p>{ stats.blocks }</p></td>
        </>
      )
    } else {
      const statusText = {
        'dnp': 'DNP',
        'forfeit': `${ stats.result == 'W' ? 'Win' : 'Loss' } by Forfeit`,
        'no-stats': 'No Stats'
      }

      return (
        <td className={ `league-table-data-name player-page stat-line no-stats ${ stats.status }` } colSpan="5" key={ playerID + "-game-stat-" + stats.gameID + "-row" }>
          <p>{ statusText[stats.status] }</p>
        </td>
      )
    }
  }

  function redirectToEditPlayer () {
    navigate(`/admin/players/${playerID}`)
  }

  function showPlayerImage () {
    if (playerView.imageURL) {
      return (
        <img className="league-player-image" src={ playerView.imageURL }></img>
      )
    } else {
      return (
        <div className="league-player-image default-image">
          <p className="default-image-initials">{ playerView.firstName[0].toUpperCase() }{ playerView.lastName[0].toUpperCase() }</p>
        </div>
      )
    }
  }

  function showAdminPanel () {
    if (!currentUser) {
      return
    }

    if (currentUser.email !== playerView.email && !config.adminEmails.includes(currentUser.email)) {
      return
    }

    return (
      <div className="league-admin-panel">
        <a onClick={ redirectToEditPlayer } type="button" className='league-admin-button'>✏️</a>
      </div>
    )
  }

  if (dataReady == false) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Players">
        <div className="league-content">
          { showAdminPanel() }
          <div className="league-player-header">
            { showPlayerImage() }
            <div>
              <p className="player-first-name-title">{ playerView.firstName.toUpperCase() }</p>
              <p className="player-last-name-title">{ playerView.lastName.toUpperCase() } </p>
            </div>
          </div>
          { playerView.career &&
          <div>
            <p className="player-number-title"><em>All-Time Record:</em> { playerView.career.wins }-{ playerView.career.losses } (<em>Playoffs</em> { playerView.career.playoffWins }-{ playerView.career.playoffLosses })</p>
          </div>
          }
          { displayBadges() }
          <div className="league-body">
            <div className="league-table-container schedule scroll-on-overflow">
              <table className="table league-table">
                <thead className="league-table-heading-hole">
                  <tr>
                    <th className="league-table-heading hole"><span>SEASON</span></th>
                    <th className="league-table-heading-name hole"><span>TEAM</span></th>
                    <th className="league-table-heading hole"><span>GP</span></th>
                    <th className="league-table-heading hole"><span>PTS</span></th>
                    <th className="league-table-heading hole"><span>RBS</span></th>
                    <th className="league-table-heading hole"><span>AST</span></th>
                    <th className="league-table-heading hole"><span>STL</span></th>
                    <th className="league-table-heading hole"><span>BLK</span></th>
                  </tr>
                </thead>
                <thead className="league-table-heading-total">
                  <tr>
                    <th className="league-table-heading-small"><span>SZN</span></th>
                    <th className="league-table-heading-small"><span>TM</span></th>
                    <th className="league-table-heading-small"><span>GP</span></th>
                    <th className="league-table-heading-small"><span>PTS</span></th>
                    <th className="league-table-heading-small"><span>RBS</span></th>
                    <th className="league-table-heading-small"><span>AST</span></th>
                    <th className="league-table-heading-small"><span>STL</span></th>
                    <th className="league-table-heading-small"><span>BLK</span></th>
                  </tr>
                </thead>
                <tbody>
                  { displayAllSeasonsAverages() }
                  { displaySeasonAverages('all') }
                </tbody>
              </table>
            </div>
          </div>
          <div className="league-body">
            <div className="league-accordion-container">
              <div className="accordion accordion-flush league-accordion" id="gameStatsAccordion">
                <div className="accordion-item league-accordion-item">
                  <h2 className="accordion-header league-accordion-header" id="gameStatsAccordionHeader">
                    <button className="accordion-button league-accordion-button collapsed league-title-skinny-small-extended-width" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                      All Games
                    </button>
                  </h2>
                  <div id="collapseOne" className="accordion-collapse collapse" aria-labelledby="gameStatsAccordionHeader" data-bs-parent="#accordionExample">
                    <div className="accordion-body league-accordion-body">
                      <div className="league-table-container schedule scroll-on-overflow">
                        <div className="league-title-examples">
                          <p className="league-title-example career-high-stat-example"><em>Player career high</em></p>
                        </div>
                        { displayAllGameStats() }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer/>
      </div>
    );
  }
}

export default Players;
